// ============================================================
// PIE — Analyzers (producción de Facts — punto de extensión)
// ============================================================
// HOY: heurísticas simples (dimensions, letterbox).
// MAÑANA: visión artificial (subjectBox), CDN, calidad.
// El núcleo NO cambia: solo se registra un analyzer nuevo.
// Los analyzers son PURAS: reciben señales vía ctx.signals y
// devuelven un subconjunto de facts. El puente con el DOM real
// (img.naturalWidth, canvas sampling) se inyecta desde el
// consumidor/presentador en PIE-I2 — aquí solo la lógica.
// ============================================================
import type { Analyzer, AnalyzeContext, SubjectFacts } from '../types';

// ── DIMENSIONS: dimensiones reales de la imagen ──
// Señales: { width, height } (del naturalWidth/Height o CDN)
export const dimensionsAnalyzer: Analyzer = {
  id: 'dimensions',
  provides: ['naturalWidth', 'naturalHeight', 'realAspect', 'orientation'],
  analyze(ctx: AnalyzeContext): Partial<SubjectFacts> {
    const s = ctx.signals ?? {};
    const w = Number(s.width);
    const h = Number(s.height);
    if (!w || !h) return {};
    const aspect = w / h;
    return {
      naturalWidth: w,
      naturalHeight: h,
      realAspect: aspect,
      orientation: aspect < 0.9 ? 'portrait' : aspect > 1.1 ? 'landscape' : 'square',
    };
  },
};

// ── LETTERBOX: análisis del entorno de la imagen ──
// Señales: { edgeSamples: [ [r,g,b], ... ] } — muestreo de píxeles
// de los bordes (4 esquinas + centros de borde), normalizado 0..255.
// Heurística: si los bordes son casi uniformes → fondo uniforme
// (expandible, sin letterbox sucio). Si varían → contenido real.
export const letterboxAnalyzer: Analyzer = {
  id: 'letterbox',
  provides: ['hasLetterbox', 'bgUniformity', 'dominantColors'],
  analyze(ctx: AnalyzeContext): Partial<SubjectFacts> {
    const s = ctx.signals ?? {};
    const samples = s.edgeSamples as Array<[number, number, number]> | undefined;
    if (!samples || samples.length === 0) return {};

    // Uniformidad: dispersión de los samples de borde
    let mean = 0;
    for (const [r, g, b] of samples) mean += (r + g + b) / 3;
    mean /= samples.length;
    let variance = 0;
    for (const [r, g, b] of samples) {
      const lum = (r + g + b) / 3;
      variance += (lum - mean) * (lum - mean);
    }
    variance /= samples.length;
    const std = Math.sqrt(variance);
    const uniformity = Math.max(0, 1 - std / 64); // std 64+ = fondo texturizado

    // Color dominante = media de los samples
    let rSum = 0, gSum = 0, bSum = 0;
    for (const [r, g, b] of samples) { rSum += r; gSum += g; bSum += b; }
    const n = samples.length;
    const dominant = `rgb(${Math.round(rSum / n)},${Math.round(gSum / n)},${Math.round(bSum / n)})`;

    return {
      hasLetterbox: uniformity > 0.85 && std < 20,  // bordes muy uniformes = bandas
      bgUniformity: uniformity,
      dominantColors: [dominant],
    };
  },
};

export const ANALYZER_REGISTRY: Record<string, Analyzer> = {
  dimensions: dimensionsAnalyzer,
  letterbox: letterboxAnalyzer,
};

/** Ejecuta todos los analyzers registrados y fusiona facts por priority. */
export async function runAnalyzers(
  ctx: AnalyzeContext,
  registry: Record<string, Analyzer> = ANALYZER_REGISTRY,
): Promise<Partial<SubjectFacts>> {
  const merged: Partial<SubjectFacts> = {};
  const entries = Object.values(registry).sort((a, b) => (b.priority ?? 0) - (a.priority ?? 0));
  for (const a of entries) {
    try {
      const result = await Promise.race([
        Promise.resolve(a.analyze(ctx)),
        new Promise<Partial<SubjectFacts>>((resolve) =>
          setTimeout(() => resolve({}), a.timeoutMs ?? 500)),
      ]);
      for (const k of a.provides) {
        const v = (result as Record<string, unknown>)[k];
        if (v !== undefined) (merged as Record<string, unknown>)[k] = v;
      }
    } catch {
      // analyzer falla → se ignora (degradación elegante, resiliencia P7)
    }
  }
  return merged;
}
